import { Link } from "react-router-dom";
import React, { useState, useEffect } from "react";
import { Tag } from "lucide-react";
import axios from "axios";

const API_BASE_URL = process.env.REACT_APP_API_URL;

const Products = () => {
  const [products, setProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const itemsPerPage = 12;

  // Fetch products from the backend
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API_BASE_URL}/products`);
        setProducts(response.data);
        console.log(response.data);
      } catch (err) {
        setError("Failed to fetch products.");
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  // Filter products based on search input
  const filteredProducts = products.filter((product) =>
    product.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Pagination logic
  const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedProducts = filteredProducts.slice(
    startIndex,
    startIndex + itemsPerPage
  );

  return (
    <div className="mx-6 min-h-[400px] mt-8">
      {/* Search Bar */}
      <div className="mb-6">
        <input
          type="text"
          placeholder="Search products..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="md:w-1/2 w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500"
        /> 
      </div>

      {/* Loading & Error Handling */}
      {loading && <p>Loading products...</p>}
      {error && <p className="text-red-500">{error}</p>}

      {/* Product Grid */}
      {!loading && !error && (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 justify-center place-items-center md:justify-start md:place-items-start ">
          {paginatedProducts.map((product) => (
            <Link
              key={product._id}
              to={`/product/${product._id}`}
              state={{ product }}
              className="transform transition duration-300 hover:scale-[1.02] h-full"
            >
              <div className="p-4 h-full w-80 border rounded-2xl shadow-md hover:shadow-xl transition-shadow bg-white border-gray-300 flex flex-col gap-3">
                {/* Product Image */}
                <div className="w-full h-44 flex items-center justify-center bg-gray-100 rounded-xl overflow-hidden">
                  <img
                    src={
                      product.photos?.[0]?.data ||
                      "https://via.placeholder.com/150"
                    }
                    alt={product.name}
                    className="max-w-full max-h-full object-contain"
                  />
                </div>

                {/* Product Info */}
                <div className="">
                  <div className="flex flex-col">
                    <h2 className="text-lg font-bold text-gray-900 truncate">
                      {product.name}
                    </h2>
                    {/* <span className="text-xs font-semibold rounded-full uppercase tracking-wide">
                      {product.productType}
                    </span> */}
                    <h3 className="text-md font-semibold text-gray-700">
                      {product.manufacturer}
                    </h3>
                  </div>
                  <hr className="border-t-1 my-2 border-dashed border-gray-500" />
                  <div className="">
                    {/* Tag */}
                    <div className="flex gap-1">
                      <div className="flex gap-2">
                        <span className="text-sm inline-flex items-center  text-gray-600">
                          <Tag className="w-3 h-3" />
                          Tags:
                        </span>
                        <div className="">
                          {product.tags.map((tag, index) => (
                            <span className="inline-flex items-center text-sm font-medium text-gray-700 border border-gray-400 px-2 py-1 rounded-md my-1 mx-1">
                              {tag}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="flex justify-center mt-6 space-x-4">
          <button
            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            className="px-4 py-2 bg-gray-300 rounded disabled:opacity-50"
          >
            Previous
          </button>
          <span className="px-4 py-2 bg-gray-200 rounded">
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={() =>
              setCurrentPage((prev) => Math.min(prev + 1, totalPages))
            }
            disabled={currentPage === totalPages}
            className="px-4 py-2 bg-gray-300 rounded disabled:opacity-50"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default Products;
